import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  FileText,
  Printer,
  ShieldCheck,
  UserCheck,
  CreditCard,
  Copyright,
  AlertTriangle,
  Lock,
  Scale,
  Mail,
} from "lucide-react";
import { useTheme } from "../../../context/ThemeContext";
import { useCompanyConfig } from "../../../hooks/useCompanyConfig";

/* ====== Theme helpers (ProductDetail ile aynı yaklaşım) ====== */
const hexToRgb = (hex: string) => {
  const h = (hex || "#ffffff").replace("#", "");
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const n = parseInt(full, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
};
const luminance = ({ r, g, b }: { r: number; g: number; b: number }) => {
  const f = (v: number) => {
    v /= 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
};

type NavItem = { id: string; label: string };

const KullaniciSozlesmesi: React.FC = () => {
  const { theme } = useTheme();
  const company = useCompanyConfig();

  const accent = theme?.accent ?? "#2563eb";
  const textPrimary = theme?.textPrimary ?? "#0f172a";
  const textSecondary = theme?.textSecondary ?? "#475569";

  const pageBg = theme?.backgroundColor || "#ffffff";
  const bgRgb = hexToRgb(pageBg);
  const isDarkBg = luminance(bgRgb) < 0.5;

  const cardBg = isDarkBg ? "rgba(17,24,39,0.92)" : "#ffffff";
  const borderCol = isDarkBg ? "rgba(255,255,255,.14)" : "rgba(15,23,42,.12)";
  const softShadow = isDarkBg
    ? "0 10px 26px rgba(0,0,0,.35)"
    : "0 10px 26px rgba(2,6,23,.08)";

  const cardStyle: React.CSSProperties = {
    backgroundColor: cardBg,
    borderColor: borderCol,
    borderWidth: 1,
    boxShadow: softShadow,
  };
  const subtleBorder: React.CSSProperties = { borderColor: borderCol };

  const lastUpdate = "23 ŞUBAT 2026";

  const nav: NavItem[] = useMemo(
    () => [
      { id: "k1", label: "1. Taraflar" },
      { id: "k2", label: "2. Konu" },
      { id: "k3", label: "3. Üyelik" },
      { id: "k4", label: "4. Sipariş/Ödeme" },
      { id: "k5", label: "5. Fikri Mülkiyet" },
      { id: "k6", label: "6. Sorumluluk" },
      { id: "k7", label: "7. Gizlilik" },
      { id: "k8", label: "8. Fesih" },
      { id: "k9", label: "9. Uyuşmazlık" },
      { id: "k10", label: "10. Yürürlük" },
      { id: "k11", label: "İletişim" },
    ],
    []
  );

  const onPrint = () => {
    if (typeof window !== "undefined") window.print();
  };

  return (
    <div style={{ backgroundColor: pageBg }}>
      {/* Print styles */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .print-page { padding: 0 !important; }
          .print-card { box-shadow: none !important; border: 1px solid #e5e7eb !important; }
          a { text-decoration: none !important; color: inherit !important; }
        }
      `}</style>

      <div className="max-w-5xl mx-auto px-4 py-6 md:py-10 print-page">
        {/* Top bar */}
        <div className="no-print mb-4 md:mb-6 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Link
              to="/"
              className="inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm font-semibold"
              style={{ ...subtleBorder, color: textPrimary, backgroundColor: cardBg }}
            >
              <ArrowLeft className="w-4 h-4" />
              Anasayfa
            </Link>

            <div className="hidden md:flex items-center gap-2 text-sm" style={{ color: textSecondary }}>
              <span className="opacity-70">/</span>
              <span>Kullanıcı Sözleşmesi</span>
            </div>
          </div>

          <button
            type="button"
            onClick={onPrint}
            className="inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm font-semibold"
            style={{ ...subtleBorder, color: textPrimary, backgroundColor: cardBg }}
          >
            <Printer className="w-4 h-4" />
            Yazdır
          </button>
        </div>

        {/* Header */}
        <div className="p-5 md:p-7 rounded-2xl border print-card" style={cardStyle}>
          <div className="flex items-start justify-between gap-4">
            <div>
              <div
                className="inline-flex items-center gap-2 text-xs font-semibold rounded-full px-3 py-1 border"
                style={{
                  ...subtleBorder,
                  color: textSecondary,
                  backgroundColor: isDarkBg ? "rgba(255,255,255,.04)" : "#fff",
                }}
              >
                <FileText className="w-4 h-4" />
                Sözleşme Metni
              </div>

              <h1 className="mt-3 text-2xl md:text-3xl font-extrabold leading-tight" style={{ color: textPrimary }}>
                KULLANICI SÖZLEŞMESİ
              </h1>

              <p className="mt-2 text-sm leading-6" style={{ color: textSecondary }}>
                <b style={{ color: textPrimary }}>Son Güncelleme:</b> {lastUpdate}
              </p>

              <p className="mt-3 text-sm leading-7" style={{ color: textSecondary }}>
                İşbu sözleşme, Kullanıcı’nın Site’yi kullanması ve/veya üyelik oluşturması ile birlikte elektronik ortamda
                taraflar arasında akdedilmiş sayılır.
              </p>
            </div>

            <div
              className="hidden md:flex items-center gap-2 text-xs font-semibold rounded-xl px-3 py-2 border"
              style={{
                ...subtleBorder,
                color: textSecondary,
                backgroundColor: isDarkBg ? "rgba(255,255,255,.04)" : "#fff",
              }}
            >
              <ShieldCheck className="w-4 h-4" style={{ color: textPrimary }} />
              Site kullanımı ile kabul
            </div>
          </div>

          <div className="mt-5 border-t" style={subtleBorder} />

          {/* Quick nav */}
          <div className="no-print mt-4 flex flex-wrap gap-2 text-xs">
            {nav.map((x) => (
              <a
                key={x.id}
                href={`#${x.id}`}
                className="rounded-full border px-3 py-1.5 hover:opacity-90 transition"
                style={{
                  borderColor: borderCol,
                  color: textSecondary,
                  backgroundColor: isDarkBg ? "rgba(255,255,255,.04)" : "#fff",
                }}
              >
                {x.label}
              </a>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="mt-6 space-y-6">
          {/* 1 */}
          <section id="k1" className="p-5 md:p-7 rounded-2xl border print-card" style={cardStyle}>
            <div className="flex items-center gap-2">
              <UserCheck className="w-5 h-5" style={{ color: textPrimary }} />
              <h2 className="text-lg md:text-xl font-bold" style={{ color: textPrimary }}>
                1. TARAFLAR
              </h2>
            </div>

            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div className="rounded-2xl border p-4 bg-white" style={{ borderColor: borderCol }}>
                <div className="text-sm font-extrabold" style={{ color: textPrimary }}>
                  1.1. {company.companyName} (&quot;{company.companyName}&quot; / &quot;Şirket&quot;)
                </div>
                <div className="mt-2 leading-7" style={{ color: textSecondary }}>
                  <div><b style={{ color: textPrimary }}>Adres:</b> {company.addressLine}</div>
                  <div><b style={{ color: textPrimary }}>Telefon:</b> {company.phoneDisplay}</div>
                  <div><b style={{ color: textPrimary }}>E-posta:</b> {company.mailDisplay}</div>
                  <div><b style={{ color: textPrimary }}>Web:</b> {company.website}</div>
                </div>
              </div>

              <div className="rounded-2xl border p-4 bg-white" style={{ borderColor: borderCol }}>
                <div className="text-sm font-extrabold" style={{ color: textPrimary }}>
                  1.2. Kullanıcı / Üye
                </div>
                <div className="mt-2 leading-7" style={{ color: textSecondary }}>
                  {company.website} internet sitesini (&quot;Site&quot;) ziyaret eden veya Site üzerinden üye olan, sipariş veren ve
                  hizmetlerden yararlanan gerçek veya tüzel kişi (Bundan sonra &quot;Kullanıcı&quot; veya &quot;Üye&quot; olarak anılacaktır).
                </div>
              </div>
            </div>
          </section>

          {/* 2 */}
          <section id="k2" className="p-5 md:p-7 rounded-2xl border print-card" style={cardStyle}>
            <h2 className="text-lg md:text-xl font-bold" style={{ color: textPrimary }}>
              2. KONU VE KAPSAM
            </h2>
            <p className="mt-3 text-sm leading-7" style={{ color: textSecondary }}>
              İşbu sözleşme, Kullanıcı’nın Site’yi kullanma, üyelik şartları, sipariş süreci, fikri mülkiyet hakları,
              sorumluluk sınırları ve tarafların hak ve yükümlülüklerini düzenler.
            </p>
          </section>

          {/* 3 */}
          <section id="k3" className="p-5 md:p-7 rounded-2xl border print-card" style={cardStyle}>
            <h2 className="text-lg md:text-xl font-bold" style={{ color: textPrimary }}>
              3. ÜYELİK VE HESAP GÜVENLİĞİ
            </h2>

            <ul className="mt-3 list-disc pl-5 space-y-2 text-sm leading-7" style={{ color: textSecondary }}>
              <li>3.1. Site üzerinden satın alma işlemi yapmak ve bazı içeriklere erişmek için üyelik oluşturulması gereklidir.</li>
              <li>
                3.2. Üyelik sırasında Kullanıcı, doğru, eksiksiz ve güncel bilgiler vermekle yükümlüdür. Yanlış bilgi verilmesinden doğabilecek tüm
                hukuki ve cezai sorumluluk Kullanıcı’ya aittir.
              </li>
              <li>
                3.3. Kullanıcı, üyelik hesap bilgilerini (kullanıcı adı, şifre) gizli tutmak ve üçüncü kişilerle paylaşmamakla yükümlüdür. Hesap üzerinden
                yapılan tüm işlemlerin sorumluluğu Kullanıcı’ya aittir.
              </li>
              <li>3.4. {company.companyName}, şüpheli aktivite tespit etmesi halinde üyeliği askıya alma veya sonlandırma hakkını saklı tutar.</li>
            </ul>

            <div
              className="mt-4 rounded-xl px-4 py-3 text-sm"
              style={{
                border: `1px solid ${borderCol}`,
                backgroundColor: isDarkBg ? "rgba(255,255,255,.04)" : "rgba(37,99,235,.06)",
                color: textSecondary,
              }}
            >
              <span className="inline-flex items-center gap-2">
                <span className="h-5 w-[3px] rounded-full" style={{ backgroundColor: accent as any }} />
                Hesabınızın güvenliği için güçlü şifre kullanmanızı öneririz.
              </span>
            </div>
          </section>

          {/* 4 */}
          <section id="k4" className="p-5 md:p-7 rounded-2xl border print-card" style={cardStyle}>
            <div className="flex items-center gap-2">
              <CreditCard className="w-5 h-5" style={{ color: textPrimary }} />
              <h2 className="text-lg md:text-xl font-bold" style={{ color: textPrimary }}>
                4. SİPARİŞ, ÖDEME VE TESLİMAT
              </h2>
            </div>

            <ul className="mt-3 list-disc pl-5 space-y-2 text-sm leading-7" style={{ color: textSecondary }}>
              <li>
                4.1. Site üzerindeki ürün fiyatları, KDV dahil fiyatlardır. {company.companyName}, önceden bildirimde bulunmak kaydıyla fiyat ve ürün bilgilerinde
                değişiklik yapma hakkını saklı tutar.
              </li>
              <li>
                4.2. Kullanıcı, sepetine eklediği ürünleri sipariş aşamasında son kez kontrol etmekle yükümlüdür. Sipariş onaylandıktan sonra {company.companyName}
                tarafından alınan sipariş iptal ve değişiklik talepleri kabul edilmeyebilir.
              </li>
              <li>4.3. Ödeme, Site üzerinde entegre güvenli ödeme sistemleri (bankalar, ödeme kuruluşları) aracılığıyla anında alınır.</li>
              <li>
                4.4. Teslimat süreleri, ürün stoğu ve kargo firmasının performansına göre değişiklik gösterebilir. Olağanüstü durumlarda (stok yokluğu,
                tedarik sorunu) {company.companyName}, Kullanıcı’yı bilgilendirerek siparişi iptal etme veya erteleme hakkına sahiptir.
              </li>
              <li>
                4.5. Tüm ürünler, mesafeli satış mevzuatı (6502 Sayılı Tüketicinin Korunması Kanunu) kapsamında 14 günlük cayma hakkına tabidir. Cayma
                hakkının kullanılabilmesi için ürünün orijinal ambalajında, hasarsız ve kullanılmamış olarak iade edilmesi gereklidir.
              </li>
            </ul>
          </section>

          {/* 5 */}
          <section id="k5" className="p-5 md:p-7 rounded-2xl border print-card" style={cardStyle}>
            <div className="flex items-center gap-2">
              <Copyright className="w-5 h-5" style={{ color: textPrimary }} />
              <h2 className="text-lg md:text-xl font-bold" style={{ color: textPrimary }}>
                5. FİKRİ MÜLKİYET HAKLARI
              </h2>
            </div>

            <ul className="mt-3 list-disc pl-5 space-y-2 text-sm leading-7" style={{ color: textSecondary }}>
              <li>
                5.1. Site’de yer alan tüm içerikler (yazılım, tasarım, logo, marka, görsel, metin vb.) {company.companyName}’nin veya lisans verdiği tarafların
                mülkiyetindedir ve 5846 sayılı Fikir ve Sanat Eserleri Kanunu ile korunmaktadır.
              </li>
              <li>
                5.2. Kullanıcı, {company.companyName}’nin yazılı izni olmaksızın Site içeriğini kopyalayamaz, çoğaltamaz, dağıtamaz, işleyemez veya ticari amaçlarla kullanamaz.
              </li>
            </ul>
          </section>

          {/* 6 */}
          <section id="k6" className="p-5 md:p-7 rounded-2xl border print-card" style={cardStyle}>
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" style={{ color: textPrimary }} />
              <h2 className="text-lg md:text-xl font-bold" style={{ color: textPrimary }}>
                6. SORUMLULUK SINIRLARI VE MUAFİYET
              </h2>
            </div>

            <ul className="mt-3 list-disc pl-5 space-y-2 text-sm leading-7" style={{ color: textSecondary }}>
              <li>
                6.1. {company.companyName}, Site’nin kesintisiz, hatasız ve güvenli şekilde çalışacağını taahhüt etmez. Teknik aksaklıklardan, bakım çalışmalarından veya
                üçüncü taraf hizmetlerindeki kesintilerden doğan zararlardan sorumlu tutulamaz.
              </li>
              <li>
                6.2. {company.companyName}, Kullanıcı’nın Site’yi hukuka aykırı amaçlarla (spam, hack, zararlı yazılım yayma, kişisel veri toplama vb.) kullanmasından doğan
                hiçbir sorumluluğu kabul etmez. Bu tür durumlarda hukuki ve cezai takip hakkı saklıdır.
              </li>
              <li>6.3. {company.companyName}, üçüncü taraf sitelerine verilen bağlantılardan (link) ve bu sitelerin içeriklerinden sorumlu değildir.</li>
            </ul>
          </section>

          {/* 7 */}
          <section id="k7" className="p-5 md:p-7 rounded-2xl border print-card" style={cardStyle}>
            <div className="flex items-center gap-2">
              <Lock className="w-5 h-5" style={{ color: textPrimary }} />
              <h2 className="text-lg md:text-xl font-bold" style={{ color: textPrimary }}>
                7. GİZLİLİK VE KİŞİSEL VERİLER
              </h2>
            </div>

            <ul className="mt-3 list-disc pl-5 space-y-2 text-sm leading-7" style={{ color: textSecondary }}>
              <li>7.1. Kullanıcı’ya ait kişisel veriler, 6698 sayılı KVKK ve Şirket’in Gizlilik Politikası uyarınca işlenir ve korunur.</li>
              <li>
                7.2. Kullanıcı, üyelik veya alışveriş sırasında kişisel verilerinin işlenmesine, Gizlilik Politikası’nda belirtilen amaçlarla sınırlı olmak kaydıyla,
                açıkça rıza gösterir.
              </li>
            </ul>
          </section>

          {/* 8 */}
          <section id="k8" className="p-5 md:p-7 rounded-2xl border print-card" style={cardStyle}>
            <h2 className="text-lg md:text-xl font-bold" style={{ color: textPrimary }}>
              8. SÖZLEŞMENİN FESHİ
            </h2>

            <ul className="mt-3 list-disc pl-5 space-y-2 text-sm leading-7" style={{ color: textSecondary }}>
              <li>8.1. Kullanıcı, hesabını dilediği zaman Site üzerinden silebilir veya {company.companyName}’ye yazılı başvuruda bulunarak üyeliğini sonlandırabilir.</li>
              <li>
                8.2. {company.companyName}, Kullanıcı’nın işbu sözleşme hükümlerini ihlal etmesi durumunda, önceden uyarıda bulunmaksızın üyeliği sonlandırma ve Site erişimini
                engelleme hakkına sahiptir.
              </li>
            </ul>
          </section>

          {/* 9 */}
          <section id="k9" className="p-5 md:p-7 rounded-2xl border print-card" style={cardStyle}>
            <div className="flex items-center gap-2">
              <Scale className="w-5 h-5" style={{ color: textPrimary }} />
              <h2 className="text-lg md:text-xl font-bold" style={{ color: textPrimary }}>
                9. UYUŞMAZLIKLARIN ÇÖZÜMÜ
              </h2>
            </div>

            <ul className="mt-3 list-disc pl-5 space-y-2 text-sm leading-7" style={{ color: textSecondary }}>
              <li>9.1. İşbu sözleşmeden doğabilecek uyuşmazlıkların çözümünde Türkiye Cumhuriyeti kanunları uygulanır.</li>
              <li>9.2. Uyuşmazlıkların çözümünde Adana Mahkemeleri ve İcra Daireleri yetkilidir.</li>
              <li>9.3. Tüketici işlemlerinde, Kullanıcı’nın Tüketici Hakem Heyeti veya Tüketici Mahkemesi nezdinde hak arama yolları saklıdır.</li>
            </ul>
          </section>

          {/* 10 */}
          <section id="k10" className="p-5 md:p-7 rounded-2xl border print-card" style={cardStyle}>
            <h2 className="text-lg md:text-xl font-bold" style={{ color: textPrimary }}>
              10. YÜRÜRLÜK
            </h2>

            <p className="mt-3 text-sm leading-7" style={{ color: textSecondary }}>
              İşbu sözleşme, Kullanıcı’nın Site’yi kullanmaya başlaması ve/veya üye olması ile yürürlüğe girer. {company.companyName},
              sözleşmeyi dilediği zaman güncelleme hakkına sahiptir. Güncel sürüm her zaman Site’de yayınlanacaktır.
            </p>
          </section>

          {/* Contact */}
          <section id="k11" className="p-5 md:p-7 rounded-2xl border print-card" style={cardStyle}>
            <div className="flex items-center gap-2">
              <Mail className="w-5 h-5" style={{ color: textPrimary }} />
              <h2 className="text-lg md:text-xl font-bold" style={{ color: textPrimary }}>
                İLETİŞİM BİLGİLERİ
              </h2>
            </div>

            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div className="rounded-2xl border p-4 bg-white" style={{ borderColor: borderCol }}>
                <div className="text-xs font-semibold" style={{ color: textSecondary }}>
                  {company.companyName}
                </div>
                <div className="mt-2 leading-7" style={{ color: textSecondary }}>
                  <div><b style={{ color: textPrimary }}>Adres:</b> {company.addressLine}</div>
                  <div><b style={{ color: textPrimary }}>Tel:</b> {company.phoneDisplay}</div>
                  <div><b style={{ color: textPrimary }}>E-posta:</b> {company.mailDisplay}</div>
                  <div><b style={{ color: textPrimary }}>Web:</b> {company.website}</div>
                </div>
              </div>

              <div className="rounded-2xl border p-4 bg-white" style={{ borderColor: borderCol }}>
                <div className="text-xs font-semibold" style={{ color: textSecondary }}>
                  Not
                </div>
                <div className="mt-2 leading-7" style={{ color: textSecondary }}>
                  Site’yi kullanmaya devam ederek işbu sözleşme şartlarını kabul etmiş sayılırsınız.
                </div>
              </div>
            </div>

            <div className="no-print mt-5 flex flex-wrap gap-2">
              <Link
                to="/"
                className="inline-flex items-center gap-2 rounded-xl border px-4 py-2 text-sm font-semibold"
                style={{ borderColor: borderCol, color: textPrimary, backgroundColor: cardBg }}
              >
                <ArrowLeft className="w-4 h-4" />
                Siteye dön
              </Link>
            </div>
          </section>

          {/* Accent footer strip */}
          <div
            className="h-1.5 rounded-full"
            style={{
              background: `linear-gradient(90deg, ${String(accent)}, rgba(249,115,22,0.9))`,
              opacity: isDarkBg ? 0.9 : 1,
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default KullaniciSozlesmesi;